import { useState, useCallback } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { payments as paymentsApi } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { Badge } from '../components/Badge.js';
import { Modal } from '../components/Modal.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { EmptyState } from '../components/EmptyState.js';
import type { PaymentSession, PaymentStats, JournalEntry } from '../types/index.js';

const STATE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'AWAITING_PAYMENT', label: 'Awaiting' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'UNDERPAID', label: 'Underpaid' },
  { value: 'REFUNDING', label: 'Refunding' },
  { value: 'FAILED', label: 'Failed' },
];

const PAGE_SIZE = 20;

export function PaymentsPage() {
  const { activeTenantId } = useTenant();
  const [stateFilter, setStateFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [journalTarget, setJournalTarget] = useState<string | null>(null);

  const statsFetcher = useCallback(
    () => (activeTenantId ? paymentsApi.stats(activeTenantId) : Promise.resolve(null as unknown as PaymentStats)),
    [activeTenantId],
  );

  const listFetcher = useCallback(
    () =>
      activeTenantId
        ? paymentsApi.list(activeTenantId, {
            state: stateFilter || undefined,
            limit: PAGE_SIZE,
            offset,
          })
        : Promise.resolve({ payments: [] as PaymentSession[] }),
    [activeTenantId, stateFilter, offset],
  );

  const { data: stats, loading: statsLoading } = useApi(statsFetcher, [activeTenantId]);
  const { data: listData, loading: listLoading, error, refetch } = useApi(listFetcher, [activeTenantId, stateFilter, offset]);

  const sessions = listData?.payments ?? [];
  const pending = stats?.stateCounts?.['AWAITING_PAYMENT'] ?? 0;
  const todayConfirmed = stats?.todayConfirmed ?? 0;

  // compute unswept display
  const unswept = stats?.unsweptSol
    ? `${Number(stats.unsweptSol).toFixed(4)} SOL`
    : '—';

  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Payments</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>All payment sessions and their current status</p>
        </div>
      </div>

      {/* stats row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value" style={{ color: 'var(--amber)' }}>{pending}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Confirmed Today</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{todayConfirmed}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unswept Balance</div>
          <div className="stat-value">{unswept}</div>
        </div>
      </div>

      {/* filters */}
      <div className="filter-row">
        {STATE_FILTERS.map((f) => (
          <div
            key={f.value}
            className={`filter-pill ${stateFilter === f.value ? 'active' : ''}`}
            onClick={() => {
              setStateFilter(f.value);
              setOffset(0);
            }}
          >
            {f.label}
          </div>
        ))}
      </div>

      {(listLoading || statsLoading) && <LoadingSpinner text="Loading payments..." />}

      {!listLoading && sessions.length === 0 && (
        <EmptyState
          title="No payment sessions"
          description="Payment sessions will appear here when users initiate purchases."
        />
      )}

      {!listLoading && sessions.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Session</th>
                <th>User</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Expected</th>
                <th>Received</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setJournalTarget(s.id)}
                >
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--t3)' }}>
                    {s.id.slice(0, 4)}...{s.id.slice(-4)}
                  </td>
                  <td>
                    <span
                      style={{ color: 'var(--t1)', fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}
                      title={s.discordUserId}
                    >
                      {s.discordUserId.length > 12
                        ? `${s.discordUserId.slice(0, 6)}...${s.discordUserId.slice(-4)}`
                        : s.discordUserId}
                    </span>
                  </td>
                  <td>
                    <Badge status={s.state} label={formatState(s.state)} />
                  </td>
                  <td style={{ color: 'var(--t2)' }}>{s.planName ?? '—'}</td>
                  <td style={{ fontFamily: 'var(--fd)', fontWeight: 600 }}>
                    {Number(s.expectedAmount).toFixed(4)}
                  </td>
                  <td style={{
                    fontFamily: 'var(--fd)',
                    fontWeight: 600,
                    color: Number(s.receivedAmount) >= Number(s.expectedAmount) ? 'var(--green)' : Number(s.receivedAmount) > 0 ? 'var(--red)' : 'var(--t3)',
                  }}>
                    {Number(s.receivedAmount).toFixed(4)}
                  </td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>{timeAgo(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* pagination */}
      {sessions.length === PAGE_SIZE && (
        <div className="pagination">
          <button
            className="btn-sm btn-ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <button
            className="btn-sm btn-ghost"
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}

      {/* journal modal */}
      {journalTarget && activeTenantId && (
        <JournalModal
          tenantId={activeTenantId}
          paymentId={journalTarget}
          onClose={() => setJournalTarget(null)}
        />
      )}
    </>
  );
}

function JournalModal({ tenantId, paymentId, onClose }: { tenantId: string; paymentId: string; onClose: () => void }) {
  const { data, loading, error } = useApi<{ journal: JournalEntry[] }>(
    () => paymentsApi.journal(tenantId, paymentId),
    [tenantId, paymentId],
  );

  return (
    <Modal open title="Payment Audit Trail" onClose={onClose} width={560}>
      <div style={{ padding: '0 0 20px' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--t3)', marginBottom: 16 }}>
          Session: {paymentId}
        </div>
        {loading && <LoadingSpinner text="Loading journal..." />}
        {error && <ErrorMessage message={error} />}
        {data?.journal.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--t3)', textAlign: 'center', padding: 20 }}>
            No journal entries yet
          </div>
        )}
        {data?.journal.map((entry) => (
          <div key={entry.id} className="feed-item">
            <div className={`feed-dot ${entry.newState === 'COMPLETED' ? 'green' : entry.newState === 'FAILED' || entry.newState === 'EXPIRED' ? 'red' : 'violet'}`} />
            <div className="feed-text">
              <Badge status={entry.previousState} label={formatState(entry.previousState)} />
              <span style={{ margin: '0 6px', color: 'var(--t3)' }}>→</span>
              <Badge status={entry.newState} label={formatState(entry.newState)} />
            </div>
            <div className="feed-time">{new Date(entry.createdAt).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function formatState(state: string): string {
  return state.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
